import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Alert, Modal, Row, Col, Form } from 'react-bootstrap';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminEntidadesPage = () => {
  const [entidades, setEntidades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonatingEntidadeId, setImpersonatingEntidadeId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntidade, setSelectedEntidade] = useState(null);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [isSavingModules, setIsSavingModules] = useState(false);
  const [modulesError, setModulesError] = useState(null);

  const { startImpersonation } = useAuthContext();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchEntidades = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/admin/entidades');
        setEntidades(response.data.entidades || []);
        setError(null);
      } catch (err) {
        setError('Não foi possível carregar a lista de empresas.');
        console.error("Erro ao buscar entidades:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntidades();
  }, []);

  const handleImpersonate = async (entidade) => {
    setImpersonatingEntidadeId(entidade.id);
    try {
      const response = await axios.post(`/api/admin/entidades/${entidade.id}/impersonate`);
      await startImpersonation(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao tentar personificar a empresa.';
      alert(errorMessage);
      console.error(error);
      setImpersonatingEntidadeId(null);
    }
  };

  const handleShowDetails = (entidade) => {
    setSelectedEntidade(entidade);
    setShowDetailsModal(true);
  };

  const handleShowModules = async (entidade) => {
    setSelectedEntidade(entidade);
    setShowModulesModal(true);
    setIsLoadingModules(true);
    setModulesError(null);

    try {
      const response = await axios.get(`/api/admin/entidades/${entidade.id}/modules`);
      setModuleCatalog(response.data.catalog || []);
      setSelectedModules(response.data.active_modules || []);
    } catch (err) {
      setModulesError(err.response?.data?.message || 'Não foi possível carregar os módulos da empresa.');
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleToggleModule = (module) => {
    if (module.required) return;

    setSelectedModules(current => {
      if (current.includes(module.key)) {
        const dependentKeys = moduleCatalog
          .filter(item => item.depends_on?.includes(module.key))
          .map(item => item.key);

        return current.filter(key => key !== module.key && !dependentKeys.includes(key));
      }

      return [...new Set([...current, module.key, ...(module.depends_on || [])])];
    });
  };

  const handleSaveModules = async () => {
    if (!selectedEntidade) return;

    setIsSavingModules(true);
    setModulesError(null);

    try {
      const response = await axios.put(
        `/api/admin/entidades/${selectedEntidade.id}/modules`,
        { modules: selectedModules }
      );
      const activeModules = response.data.active_modules || [];

      setSelectedModules(activeModules);
      setEntidades(current => current.map(entidade => entidade.id === selectedEntidade.id
        ? { ...entidade, modulosAtivos: activeModules }
        : entidade));
      setShowModulesModal(false);
    } catch (err) {
      setModulesError(err.response?.data?.message || 'Não foi possível salvar os módulos da empresa.');
    } finally {
      setIsSavingModules(false);
    }
  };

  const groupedModules = moduleCatalog.reduce((groups, module) => {
    const group = module.group || 'Outros';
    groups[group] = [...(groups[group] || []), module];
    return groups;
  }, {});

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Ativa': return 'success';
      case 'Inadimplente': return 'warning';
      case 'Cancelada': return 'danger';
      case 'Trial': return 'info';
      case 'Inativa': return 'secondary';
      case 'Inativo': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid>
      <h1 className="mb-4">Gerenciamento de Empresas</h1>
      <Card>
        <Card.Header>
          Lista de Empresas Cadastradas
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Nome da Empresa</th>
                  <th>CNPJ</th>
                  <th className="text-center">Status da Assinatura</th>
                  <th className="text-center">Status</th>
                  <th>Data de Cadastro</th>
                  <th className="text-center">Corpo Clínico</th>
                  <th className="text-center">Pacientes Ativos</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {entidades.map(entidade => (
                  <tr key={entidade.id}>
                    <td>{entidade.id}</td>
                    <td>{entidade.nome}</td>
                    <td>{entidade.cnpj}</td>
                    <td className="text-center">
                      <Badge bg={getStatusBadge(entidade.statusAssinatura)}>
                        {entidade.statusAssinatura}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge pill bg={getStatusBadge(entidade.status)} style={{ fontSize: '0.9em' }}>
                        &nbsp;
                      </Badge>
                    </td>
                    <td>{new Date(entidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                    <td className="text-center">{entidade.totalCorpoClinico}</td>
                    <td className="text-center">{entidade.totalPacientesAtivos}</td>
                    <td className="text-center">
                      <Button variant="outline-info" size="sm" className="me-2" title="Ver Detalhes" onClick={() => handleShowDetails(entidade)}>
                        Ver
                      </Button>
                      <Button variant="outline-primary" size="sm" className="me-2" title="Configurar módulos" onClick={() => handleShowModules(entidade)}>
                        Módulos
                      </Button>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        title="Personificar (logar como Representante Legal desta clínica)"
                        onClick={() => handleImpersonate(entidade)}
                        disabled={impersonatingEntidadeId === entidade.id}
                      >
                        {impersonatingEntidadeId === entidade.id ? (
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          'Personificar'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Detalhes da Empresa */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntidade && (
            <Row>
              <Col md={6}>
                <p><strong>ID:</strong> {selectedEntidade.id}</p>
                <p><strong>Nome:</strong> {selectedEntidade.nome}</p>
                <p><strong>CNPJ:</strong> {selectedEntidade.cnpj}</p>
                <p><strong>Data de Cadastro:</strong> {new Date(selectedEntidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
              </Col>
              <Col md={6}>
                <p><strong>Status da Empresa:</strong> <Badge bg={getStatusBadge(selectedEntidade.status)}>{selectedEntidade.status}</Badge></p>
                <p><strong>Status da Assinatura:</strong> <Badge bg={getStatusBadge(selectedEntidade.statusAssinatura)}>{selectedEntidade.statusAssinatura}</Badge></p>
                <p><strong>Total Corpo Clínico:</strong> {selectedEntidade.totalCorpoClinico}</p>
                <p><strong>Total Pacientes Ativos:</strong> {selectedEntidade.totalPacientesAtivos}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fechar
          </Button>
          <Button variant="primary" onClick={() => alert('Função de edição a ser implementada.')}>
            Editar Clínica
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModulesModal} onHide={() => !isSavingModules && setShowModulesModal(false)} size="lg">
        <Modal.Header closeButton={!isSavingModules}>
          <Modal.Title>Módulos da empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntidade && (
            <p className="text-muted mb-3">
              Configure os recursos disponíveis para <strong>{selectedEntidade.nome}</strong>.
              As permissões dos funcionários continuam sendo controladas pelos perfis de acesso.
            </p>
          )}

          {modulesError && <Alert variant="danger">{modulesError}</Alert>}

          {isLoadingModules ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            Object.entries(groupedModules).map(([group, modules]) => (
              <Card key={group} className="mb-3">
                <Card.Header className="fw-semibold">{group}</Card.Header>
                <Card.Body className="py-2">
                  <Row>
                    {modules.map(module => (
                      <Col md={6} key={module.key} className="py-2">
                        <Form.Check
                          type="switch"
                          id={`entity-module-${module.key}`}
                          label={module.label}
                          checked={module.required || selectedModules.includes(module.key)}
                          disabled={module.required || isSavingModules}
                          onChange={() => handleToggleModule(module)}
                        />
                        {module.required && (
                          <small className="text-muted">Módulo essencial</small>
                        )}
                        {!module.required && module.depends_on?.length > 0 && (
                          <small className="text-muted d-block">
                            Inclui: {module.depends_on
                              .map(key => moduleCatalog.find(item => item.key === key)?.label || key)
                              .join(' e ')}
                          </small>
                        )}
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={isSavingModules} onClick={() => setShowModulesModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={isLoadingModules || isSavingModules || Boolean(modulesError)} onClick={handleSaveModules}>
            {isSavingModules ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Salvando...</>
            ) : 'Salvar módulos'}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default AdminEntidadesPage;
